import { useState, useEffect } from 'react'

export type Currency = 'MXN' | 'USD'

export function useCurrency() {
  const [currency, setCurrency] = useState<Currency>(() => {
    // Check localStorage cache first for instant load
    if (typeof window !== 'undefined') {
      const cached = localStorage.getItem('user_currency')
      if (cached === 'MXN' || cached === 'USD') return cached
    }
    return 'MXN'
  })
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // If we have a cached value less than 24h old, skip the API call
    const cachedTime = localStorage.getItem('user_currency_ts')
    if (cachedTime && Date.now() - parseInt(cachedTime) < 86400000) {
      setLoading(false)
      return
    }

    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), 3000) // 3s timeout

    const fetchCurrency = async () => {
      try {
        const response = await fetch('https://ipapi.co/country_code/', {
          signal: controller.signal,
        })
        const countryCode = await response.text()
        const trimmed = countryCode.trim().toUpperCase()

        // Latin American & Spanish-speaking countries → MXN
        const mxnCountries = [
          'MX', // México
          'GT', // Guatemala
          'HN', // Honduras
          'SV', // El Salvador
          'NI', // Nicaragua
          'CR', // Costa Rica
          'PA', // Panamá
          'CO', // Colombia
          'VE', // Venezuela
          'EC', // Ecuador
          'PE', // Perú
          'BO', // Bolivia
          'PY', // Paraguay
          'UY', // Uruguay
          'CL', // Chile
          'AR', // Argentina
          'DO', // República Dominicana
          'CU', // Cuba
          'PR', // Puerto Rico
        ]

        const detected: Currency = mxnCountries.includes(trimmed)
          ? 'MXN'
          : 'USD'
        setCurrency(detected)
        localStorage.setItem('user_currency', detected)
        localStorage.setItem('user_currency_ts', Date.now().toString())
      } catch {
        // Silently default to MXN on error/timeout
      } finally {
        clearTimeout(timeout)
        setLoading(false)
      }
    }

    fetchCurrency()
    return () => {
      controller.abort()
      clearTimeout(timeout)
    }
  }, [])

  return { currency, loading }
}
